import React from 'react';
import { Paperclip, ExternalLink, FileText, Truck } from 'lucide-react';

const getFileNameFromUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/');
    return pathSegments[pathSegments.length - 1];
  } catch (error) {
    return url; // Return original URL if it's not a valid URL
  }
};

const QCPreview = ({ formData, printRef, onConfirmDispatch, isSaving }) => {
  // --- STYLES (moved from QualityCheckDetail.js) ---
  const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minHeight: '1100px', width: '100%', boxSizing: 'border-box', color: '#000' };
  const headerTableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '15px', border: '1px solid #000' };
  const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '13px' };
  const labelCellStyle = { ...headerTableCellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2' };
  const valueCellStyle = { ...headerTableCellStyle, backgroundColor: '#fff' };
  const pdfTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '15px', border: '1px solid #000' };
  const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' };
  const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center' };
  const signLineStyle = { borderTop: '1px solid #000', width: '180px', textAlign: 'center', paddingTop: '10px', fontSize: '12px', fontWeight: 'bold' };

  const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
  
  const attachmentCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    textDecoration: 'none',
    color: '#374151',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#93c5fd',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
  };
  
  const attachmentIconStyle2 = (bg, color) => ({
    padding: '8px',
    backgroundColor: bg,
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: color,
  });
  
  const attachmentLabelStyle = {
    fontSize: '11px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    marginBottom: '2px',
  };
  
  const attachmentNameStyle = {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#1e293b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <>
      <div ref={printRef} style={pdfContainerStyle}>
          {/* Header Table */}
          <table style={headerTableStyle}>
            <tbody>
              <tr><td colSpan="5" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800', fontSize: '18px' }}>PACKAGING/BILL OF MATERIAL</td></tr>
              <tr>
                <td style={labelCellStyle}>Document date</td><td style={valueCellStyle}>{formData.mocDate}</td>
                <td style={labelCellStyle}>Document No.</td><td style={valueCellStyle}>{formData.mocNo}</td>
                <td style={valueCellStyle}><strong>Order No:</strong> {formData.orderNumber || '---'}</td>
              </tr>
              <tr>
                <td style={labelCellStyle}>Type of MOC</td><td style={valueCellStyle}>{formData.typeOfMOC}</td>
                <td style={labelCellStyle}>Document type</td><td style={valueCellStyle}>{formData.documentType}</td>
                <td style={{...valueCellStyle, backgroundColor: '#fdf2f2'}}><strong>ALW:</strong> {formData.aluminiumWeight} kg</td>
              </tr>
              <tr>
                <td style={labelCellStyle}>PO Number</td><td style={valueCellStyle}>{formData.poNumber}</td>
                <td style={labelCellStyle}>PO DATE</td><td style={valueCellStyle}>{formData.poDate}</td>
                <td style={valueCellStyle}><strong>Warehouse:</strong> {formData.warehouse}</td>
              </tr>
              <tr>
                <td style={labelCellStyle}>Consignee</td><td colSpan="3" style={{...valueCellStyle, fontWeight: 'bold'}}>{formData.customerName}</td>
                <td rowSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', verticalAlign: 'middle', width: '120px' }}>
                  <strong>Total Meter</strong><br /><span style={{ fontSize: '30px', fontWeight: '900' }}>{formData.totalMeterNumber}</span>
                </td>
              </tr>
              <tr><td style={labelCellStyle}>Shipping Address</td><td colSpan="3" style={{ ...valueCellStyle, height: '60px', verticalAlign: 'top' }}>{formData.shippingAddress}</td></tr>
              <tr><td style={labelCellStyle}>Material Code</td><td colSpan="3" style={valueCellStyle}>{formData.materialCode}</td></tr>
              <tr><td style={labelCellStyle}>Product Summary</td><td colSpan="3" style={valueCellStyle}>{formData.productSummary}</td></tr>
            </tbody>
          </table>

          {/* Component Table */}
          <table style={pdfTableStyle}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={pdfThStyle}>S.No</th><th style={pdfThStyle}>SKU</th><th style={{ ...pdfThStyle, textAlign: 'left' }}>Item Name</th><th style={pdfThStyle}>New Qty</th><th style={pdfThStyle}>Rental Qty</th><th style={pdfThStyle}>Total Weight</th>
              </tr>
            </thead>
            <tbody>
              {formData.components?.map((c, i) => (
                <tr key={i}>
                  <td style={pdfTdStyle}>{i + 1}</td><td style={pdfTdStyle}>{c.sku}</td><td style={{ ...pdfTdStyle, textAlign: 'left' }}>{c.name}</td><td style={pdfTdStyle}>{c.newQty || 0}</td><td style={pdfTdStyle}>{c.rentalQty || 0}</td><td style={pdfTdStyle}>{((parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)) * parseFloat(c.weight || 0)).toFixed(2)} kg</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
                <td colSpan="5" style={{ ...pdfTdStyle, textAlign: 'right' }}>TOTAL WEIGHT</td><td style={pdfTdStyle}>{parseFloat(formData.totalWeight || 0).toFixed(2)} kg</td>
              </tr>
            </tbody>
          </table>

          {/* Material Checklist */}
          <table style={headerTableStyle}>
            <tbody>
              <tr><td style={labelCellStyle}>Stickers</td><td style={valueCellStyle}>{formData.materialChecklist?.stickers ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Laser Marking</td><td style={valueCellStyle}>{formData.materialChecklist?.laserMarking ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Material Wrapping</td><td style={valueCellStyle}>{formData.materialChecklist?.materialWrapping ? 'YES' : 'NO'}</td></tr>
              <tr><td style={labelCellStyle}>Instructions</td><td colSpan="5" style={{ ...valueCellStyle, minHeight: '50px' }}>{formData.materialChecklist?.materialInstructions || 'None'}</td></tr>
            </tbody>
          </table>

          {/* QC Verification Section */}
          <table style={headerTableStyle}>
            <tbody>
              <tr>
                <td style={labelCellStyle}>Prepared by</td><td style={valueCellStyle}>{formData.preparedBy}</td>
                <td rowSpan="6" style={{ ...headerTableCellStyle, verticalAlign: 'top', fontSize: '11px', width: '45%', borderLeft: '2px solid #000' }}>
                  <div style={{fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline'}}>NOTICE FOR SALES TEAM:</div>
                  Sales Team, please verify the Material Quantity Against your BOM Before Signing the MOC. Any Discrepancies in the MOC Will Not Be the Responsibility of the Operations Team, As It Has Been Prepared Exactly As Per Your BOM.
                </td>
              </tr>
              <tr><td style={labelCellStyle}>Qty Verified By (Approval)</td><td style={valueCellStyle}>{formData.quantityVerifiedBy}</td></tr>
              <tr><td style={labelCellStyle}>Qty Verified By (Dispatch)</td><td style={valueCellStyle}>{formData.quantityVerifiedByDispatch}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Number</td><td style={valueCellStyle}>{formData.vehicleNumber}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Type</td><td style={valueCellStyle}>{formData.vehicleType || '---'}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle In Time</td><td style={valueCellStyle}>{formData.vehicleInTime}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Out Time</td><td style={valueCellStyle}>{formData.vehicleOutTime}</td></tr>
              <tr><td style={labelCellStyle}>Date</td><td style={valueCellStyle}>{formData.vehicleDate}</td><td style={{ ...headerTableCellStyle, textAlign: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f2f2f2' }}>SIGNATURE</td></tr>
            </tbody>
          </table>
        </div>

        {/* Action Button at bottom of preview */}
        {/* {formData.dispatchStatus !== 'dispatched' && onConfirmDispatch && (
          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
            <button 
              onClick={onConfirmDispatch} 
              disabled={isSaving}
              style={{
                padding: '12px 30px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '16px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}
            >
              <Truck size={20} /> Confirm Final Dispatch
            </button>
          </div>
        )} */}

        {(formData.vehicleProofUrl || formData.deliveryChallanUrl || formData.bomSignedUrl) && (
          <div style={{...cardStyle, marginTop: '20px', maxWidth: '1100px', margin: '20px auto', animation: 'fadeIn 0.3s'}}>
            <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px'}}>
              <Paperclip size={20} color="#2563eb"/> Document Attachments
            </h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '15px'}}>
              {formData.vehicleProofUrl && (
                <a href={formData.vehicleProofUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                  <div style={attachmentIconStyle2('#e0f2fe', '#0284c7')}><FileText size={20}/></div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={attachmentLabelStyle}>Vehicle Proof</div>
                    <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.vehicleProofUrl)}</div>
                  </div>
                  <ExternalLink size={18} color="#6b7280"/>
                </a>
              )}
              {formData.deliveryChallanUrl && (
                <a href={formData.deliveryChallanUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                  <div style={attachmentIconStyle2('#ede9fe', '#5b21b6')}><FileText size={20}/></div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={attachmentLabelStyle}>Delivery Challan</div>
                    <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.deliveryChallanUrl)}</div>
                  </div>
                  <ExternalLink size={18} color="#6b7280"/>
                </a>
              )}
              {formData.bomSignedUrl && (
                <a href={formData.bomSignedUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                  <div style={attachmentIconStyle2('#fee2e2', '#991b1b')}><FileText size={20}/></div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={attachmentLabelStyle}>BOM (Signed)</div>
                    <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.bomSignedUrl)}</div>
                  </div>
                  <ExternalLink size={18} color="#6b7280"/>
                </a>
              )}
            </div>
          </div>
        )}
    </>
  );
};

export default QCPreview;
